import React, { useState, useEffect } from "react";
import IntlMessages from "@iso/components/utility/intlMessages";
import ObservationModalWrapper from "./ObservationModal.styles";
import { Form, Input, Button, message, Select, Typography } from "antd";
import { observationService } from "@iso/services";
import { useSelector, useDispatch } from "react-redux";
import modalActions from "@iso/redux/modal/actions";
import "react-tagsinput/react-tagsinput.css";
import FileUploader from "@iso/components/FileUploader/FileUploader";
import { FileStorage } from "@iso/constants/UploadFile";

const { closeModal } = modalActions;
const EXPIRY_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
  { value: 120, label: "120 days" },
];
const { Paragraph } = Typography;

const ObservationModal = (props) => {
  const activePermission = useSelector(
    (state) => state.permission.activePermission
  );

  const [form] = Form.useForm();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const modalVisibility = useSelector((state) => state.modal.modalVisibility);
  const modalData = useSelector((state) => state.modal.modalData);
  const dispatch = useDispatch();
  const [dataObservation, setDataObservation] = useState({});
  const [observationImages, setObservationImages] = useState([]);
  const [uploaderSeed, setUploaderSeed] = useState(0);

  useEffect(() => {
    if (modalData.type === "observation") {
      if (JSON.stringify(modalData.object) !== "{}") {
        if (modalData.object) {
          setDataObservation(modalData.object);
        }
        setFields([
          {
            name: ["observation", "title"],
            value: modalData.object.title || "",
          },
          {
            name: ["observation", "comment"],
            value: modalData.object.comment || "",
          },
          {
            name: ["observation", "primary_object_id"],
            value: modalData.object.primary_object_id || 0,
          },
          {
            name: ["observation", "expiry_days"],
            value:
              typeof modalData.object.expiry_days === "number"
                ? modalData.object.expiry_days
                : 30,
          },
        ]);
      } else {
        setFields([
          {
            name: ["observation", "title"],
            value: "",
          },
          {
            name: ["observation", "comment"],
            value: "",
          },
          {
            name: ["observation", "primary_object_id"],
            value: props.dataObject ? props.dataObject.id : 0,
          },
          {
            name: ["observation", "expiry_days"],
            value: 30,
          },
        ]);
      }
    }
    setObservationImages(
      modalData.object && modalData.object.images ? modalData.object.images : []
    );
    setUploaderSeed(Date.now());
  }, [props.propertyId, modalVisibility, modalData]);

  const cancelModal = () => {
    dispatch(closeModal());
    if (modalData.layer) {
      modalData.featureGroup.leafletElement.removeLayer(modalData.layer);
    }
    setLoading(false);
    form.resetFields();
  };

  const handleSubmit = (e) => {
    setLoading(true);

    form
      .validateFields()
      .then((values) => {
        onStore(values);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
        setLoading(false);
      });
  };

  const onStore = (values) => {
    const selectedExpiry =
      values?.observation?.expiry_days !== undefined
        ? values.observation.expiry_days
        : null;
    const data = {
      observation: {
        ...values.observation,
        expiry_days: selectedExpiry,
        point: dataObservation.point,
        property_id: props.propertyId,
        image_ids: observationImages.map(image => image.id),
      },
    };
    observationService.storeOrUpdateObservation(data, dataObservation.id).then((res) => {
      if (res.code === "0000") {
        message.success("Your observation has been recorded, and now is waiting for approval");
        props.onSaved(res.observation)
        window.dispatchEvent(
          new CustomEvent("observation:refresh", {
            detail: { propertyId: props.propertyId },
          })
        );
        cancelModal();
        form.resetFields();
      } else {
        setLoading(false);
      }
    });
  };

  const editLocation = () => {
    props.editLocation();
    cancelModal();
  }

  const handleOnUploadSuccess = (image) => {
    setObservationImages(prev => {
      const exist = prev.some(item => item.id === image.id);
      if (exist) {
        return prev;
      }
      return [...prev, image];
    });
  };

  const handleOnRemoveImage = (file) => {
    const removedId = file?.id || file?.response?.image?.id;
    if (removedId) {
      setObservationImages(prev => prev.filter(image => image.id !== removedId));
    }
  };

  const existingFileList = React.useMemo(() => {
    return observationImages.map((image) => ({
      uid: `observation-image-${image.id}`,
      id: image.id,
      name: image.file_name || image.path?.split('/').pop() || `image-${image.id}`,
      status: "done",
      url: image.full_path,
      thumbUrl: image.full_path,
    }));
  }, [observationImages]);

  return (
    <ObservationModalWrapper
      className="user-access-modal"
      forceRender={true}
      visible={modalVisibility && modalData.type === "observation"}
      title={
        dataObservation.id ? (
          <IntlMessages id="propertyMapPage.editObservation" />
        ) : (
          <IntlMessages id="propertyMapPage.addObservation" />
        )
      }
      onCancel={cancelModal}
      maskClosable={false}
      footer={[
        (activePermission == "owner" || activePermission == "admin"|| activePermission == "modify") && dataObservation.id && <Button key="editLocation" onClick={editLocation} style={{float: 'left'}} className="cancel-button">
          {<IntlMessages id="propertyPage.modal.move" />}
        </Button>,
        <Button key="back" onClick={cancelModal} className="cancel-button">
          {<IntlMessages id="propertyPage.modal.cancel" />}
        </Button>,
        (activePermission == "owner" ||
          activePermission == "admin" ||
          activePermission == "modify") && (
          <Button
            key="submit"
            className="btn-success"
            type="primary"
            onClick={handleSubmit}
            loading={loading}
          >
            {<IntlMessages id="propertyPage.modal.save" />}
          </Button>
        ),
      ]}
    >
      {
        <Form
          form={form}
          name="addEditTask"
          layout="vertical"
          scrollToFirstError
          fields={fields}
        >
          <div className="form-body">
            <Form.Item
              name={["observation", "title"]}
              className="isoInputWrapper"
              label="Title"
              rules={[
                {
                  required: true,
                  message: "This field is required",
                },
                {
                  whitespace: true,
                  message: "Please input your Title",
                },
              ]}
            >
              <Input size="large" placeholder="Input observation title" />
            </Form.Item>
            <Form.Item name={["observation", "comment"]} label="Comment">
              <Input.TextArea />
            </Form.Item>
            <Form.Item name={["observation", "expiry_days"]} label="Expiry">
              <Select
                size="large"
                options={EXPIRY_OPTIONS}
                allowClear
                placeholder="Select expiry timeframe"
              />
            </Form.Item>
            <Form.Item name={["attachment"]} label="Attachment">
              <FileUploader
                key={uploaderSeed}
                type='observation'
                storage={FileStorage.s3}
                multiple={true}
                isCompressFile={true}
                defaultFileList={existingFileList}
                handleOnSuccess={handleOnUploadSuccess}
                handleOnRemove={handleOnRemoveImage}
              />
            </Form.Item>
          </div>
        </Form>
      }
    </ObservationModalWrapper>
  );
};

export default ObservationModal;
